import { IDockviewPanelProps } from "dockview-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-quill-new/dist/quill.snow.css";
import Plot from "react-plotly.js";
import { underlyingLayout, optIvLayout, optPricingLayout } from "./layouts";
import {
  IGetTotalViewIvRequest,
  IGetTotalViewIvResponse,
  IGetTradesResponse,
  IPlaceOrderResponse,
} from "./interfaces";
import { ContractSelector } from "./ContractSelector";
import { ActiveTrades } from "./ActiveTrades";
import { getIvModelParams, OrderInput } from "./OrderInput";
import { GetTotalIvForm } from "./GetTotalIvForm";
import {
  useAccountsStore,
  useFrontendOptionState,
  useLiveMarketDataStore,
  useOrderModifyDataStore,
  useUnderlyingOrderModifyDataStore,
  usePositionsStore,
  useTradesStore,
} from "./store";
import { IBKRStoreProvider } from "./IBKRStoreProvider";
import Plotter from "../utils/Plotter";
import { UnderlyingOrder } from "./UnderlyingOrder";

const TARGET_TIME_NYC = "09:30"; // 9:30 AM in New York time

export function IBKR(props: IDockviewPanelProps) {
  return (
    <IBKRStoreProvider>
      <IBKRInner {...props} />
    </IBKRStoreProvider>
  );
}

function IBKRInner(props: IDockviewPanelProps) {
  const [optTotalViewReq, setOptTotalViewReq] =
    useState<IGetTotalViewIvRequest>(() => {
      try {
        const saved = localStorage.getItem("ibkr_totalViewReq");
        if (saved) return JSON.parse(saved);
      } catch {}
      return {
        ticker: "kyiv",
        right: "c",
        start: "10",
        directional_limit: "5",
        skip_expiries: "0",
        number_of_expiries: "1",
      };
    });

  useEffect(() => {
    localStorage.setItem("ibkr_totalViewReq", JSON.stringify(optTotalViewReq));
  }, [optTotalViewReq]);

  // Additional "Get" forms stacked under the primary one, so the user can
  // keep a few instruments' request params around and alternate between
  // them by pressing each form's own Get button.
  const [extraTotalViewForms, setExtraTotalViewForms] = useState<
    { id: string; req: IGetTotalViewIvRequest }[]
  >(() => {
    try {
      const saved = localStorage.getItem("ibkr_extraTotalViewReqs");
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });

  useEffect(() => {
    localStorage.setItem(
      "ibkr_extraTotalViewReqs",
      JSON.stringify(extraTotalViewForms),
    );
  }, [extraTotalViewForms]);

  function addTotalViewFormAfter(
    sourceReq: IGetTotalViewIvRequest,
    afterIndex: number,
  ) {
    const newForm = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2),
      req: { ...sourceReq },
    };
    setExtraTotalViewForms((prev) => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, newForm);
      return next;
    });
  }

  function removeTotalViewForm(id: string) {
    setExtraTotalViewForms((prev) => prev.filter((f) => f.id !== id));
  }

  function updateTotalViewForm(id: string, req: IGetTotalViewIvRequest) {
    setExtraTotalViewForms((prev) =>
      prev.map((f) => (f.id === id ? { ...f, req } : f)),
    );
  }

  // websocket
  const ws = useRef<WebSocket | null>(null);

  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [wsConnected, setWsConnected] = useState(false);
  const [lastConnectionTime, setLastConnectionTime] = useState<string>("");
  const [progressMessage, setProgressMessage] = useState<string>("");

  // charts
  const [underlyingConId, setUnderlyingConId] = useState<number | null>(null);
  const [optIvData, setOptIvData] = useState<any[]>([]);
  const [optPriceData, setOptPriceData] = useState<any[]>([]);
  const [underlyingData, setUnderlyingData] = useState<any[]>([]);

  const [plotsLoading, setPlotsLoading] = useState<boolean>(false);

  const orderModifyDataStore = useOrderModifyDataStore();
  const liveMarketDataStore = useLiveMarketDataStore();
  const tradesStore = useTradesStore();
  const frontendOptionState = useFrontendOptionState();
  const positionsStore = usePositionsStore();
  const accountsStore = useAccountsStore();
  const underlyingOrderModifyDataStore = useUnderlyingOrderModifyDataStore();

  // Refs updated every render so the keyboard handler always sees latest state
  const latestOptionsRef = useRef(frontendOptionState.frontendOptionState);
  latestOptionsRef.current = frontendOptionState.frontendOptionState;
  const latestTradesRef = useRef(tradesStore.trades);
  latestTradesRef.current = tradesStore.trades;
  const cancelOrderRef = useRef<(orderId: number) => void>(() => {});
  const underlyingChartRef = useRef<HTMLDivElement>(null);
  const optPriceChartRef = useRef<HTMLDivElement>(null);
  const pendingOrderRef = useRef<{
    conId: string;
    action: string;
    readable_name: string;
    price: string;
    size: string;
  } | null>(null);
  const pendingOrderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const pendingUnderlyingOrderRef = useRef<{
    conId: string;
    action: string;
    price: string;
    size: string;
  } | null>(null);
  const pendingUnderlyingOrderTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const alertsFiredRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    function playBeeps(count: number) {
      const ctx = new AudioContext();
      for (let i = 0; i < count; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.value = 880;
        const t = ctx.currentTime + i * 1.12;
        gain.gain.setValueAtTime(1.5, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.72);
        osc.start(t);
        osc.stop(t + 0.72);
      }
      setTimeout(() => ctx.close(), count * 1120 + 300);
    }

    const interval = setInterval(() => {
      const now = new Date();
      const nycStr = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(now);

      const [h, m, s] = nycStr.split(":").map(Number);
      const [th, tm] = TARGET_TIME_NYC.split(":").map(Number);
      const nowSecs = h * 3600 + m * 60 + s;
      const targetSecs = th * 3600 + tm * 60;
      const diff = targetSecs - nowSecs;

      if (diff >= 115 && diff <= 125 && !alertsFiredRef.current.has("2min")) {
        alertsFiredRef.current.add("2min");
        playBeeps(1);
      } else if (
        diff >= 55 &&
        diff <= 65 &&
        !alertsFiredRef.current.has("1min")
      ) {
        alertsFiredRef.current.add("1min");
        playBeeps(2);
      } else if (
        diff >= 25 &&
        diff <= 35 &&
        !alertsFiredRef.current.has("30sec")
      ) {
        alertsFiredRef.current.add("30sec");
        playBeeps(3);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    resetState();
    refreshSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!wsConnected) return;

    const interval = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ function: "ping" }));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [wsConnected]);

  useEffect(() => {
    // notify backend to calculate IV whenever live market data updates
    for (let update of liveMarketDataStore.liveMarketData) {
      if (
        update.readable_name.length <= 6 && // check for the underlying
        update.last !== undefined && // valid price
        ws.current // connected to ws
      ) {
        try {
          const _rn = orderModifyDataStore.orderModifyData.readable_name;
          if (
            !_rn ||
            !orderModifyDataStore.orderModifyData.price ||
            _rn.length <= 6
          )
            break;
          const ivModelParams = getIvModelParams(
            orderModifyDataStore.orderModifyData.readable_name,
            orderModifyDataStore.orderModifyData.price,
            update.last,
          );

          ws.current.send(
            JSON.stringify({
              function: "option_solver",
              ...ivModelParams,
            }),
          );
        } catch (e) {
          console.error("Error calculating IV: ", e);
        }

        break; // only need the underlying
      }
    }
  }, [liveMarketDataStore.liveMarketData]);

  function plotIV(totalViewIv: IGetTotalViewIvResponse) {
    const optIvTraces: any[] = [];
    const colors = ["blue", "green", "orange", "red", "purple"];

    for (let i = 0; i < totalViewIv.options.length; i++) {
      const traceName = totalViewIv.options[i].readable_name;

      const trace = {
        type: "scatter",
        // %Y-%m-%dT%H:%M:%S => already in correct format for Date constructor
        x: totalViewIv.options[i].opt_pricing.map((d) => new Date(d.time)),
        y: totalViewIv.options[i].opt_pricing.map((d) => d.iv),
        customdata: totalViewIv.options[i].opt_pricing.map((d) => ({
          ...d,
          traceName: traceName,
        })),
        hovertemplate:
          "%{customdata.traceName}<br>" +
          "IV: <b>%{customdata.iv:.2f}</b> Volume: <b>%{customdata.volume}</b><br>" +
          "Price: <b>%{customdata.average:.2f}</b> (%{customdata.low:.2f} - %{customdata.high:.2f})<br>" +
          "Underlying: <b>%{customdata.underlying_average:.2f}</b> (%{customdata.underlying_low:.2f} - %{customdata.underlying_high:.2f})<br>" +
          "Time: <b>%{x}</b><br>" +
          "<extra></extra>", // remove secondary box
        name: traceName,
        line: {
          color: colors[i % colors.length],
          width: 1,
        },

        mode: "lines+markers",
        marker: {
          size: 3, // bigger circle size (default is ~6)
          symbol: "circle", // can be "circle", "square", "diamond", etc
        },
      };

      optIvTraces.push(trace);
    }

    setOptIvData(optIvTraces);

    // plot option prices
    const optPriceTraces: any[] = [];

    for (let i = 0; i < totalViewIv.options.length; i++) {
      const traceName = totalViewIv.options[i].readable_name;

      const trace = {
        type: "scatter",
        // %Y-%m-%dT%H:%M:%S => already in correct format for Date constructor
        x: totalViewIv.options[i].opt_pricing.map((d) => new Date(d.time)),
        y: totalViewIv.options[i].opt_pricing.map((d) => d.average),
        customdata: totalViewIv.options[i].opt_pricing.map((d) => ({
          ...d,
          traceName: traceName,
        })),
        hovertemplate:
          "%{customdata.traceName}<br>" +
          "Price: <b>%{customdata.average:.2f}</b> (%{customdata.low:.2f} - %{customdata.high:.2f})<br>" +
          "IV: <b>%{customdata.iv:.2f}</b> Volume: <b>%{customdata.volume}</b><br>" +
          "Underlying: <b>%{customdata.underlying_average:.2f}</b> (%{customdata.underlying_low:.2f} - %{customdata.underlying_high:.2f})<br>" +
          "Time: <b>%{x}</b><br>" +
          "<extra></extra>", // remove secondary box
        name: traceName,
        line: {
          color: colors[i % colors.length],
          width: 1,
        },

        mode: "lines+markers",
        marker: {
          size: 3, // bigger circle size (default is ~6)
          symbol: "circle", // can be "circle", "square", "diamond", etc
        },
      };

      optPriceTraces.push(trace);
    }

    setOptPriceData(optPriceTraces);

    const lineTrace: any = {
      x: totalViewIv.underlying.map((d) => new Date(d.time)),
      y: totalViewIv.underlying.map((d) => d.average),
      type: "scatter",
      mode: "lines",
      opacity: 0.1,
      line: { color: "#1f77b4" },
      xaxis: "x",
      yaxis: "y",
      showlegend: false,
    };

    const underlyingCandlesticks = {
      showlegend: false,
      x: totalViewIv.underlying.map((d) => new Date(d.time)),

      close: totalViewIv.underlying.map((d) => d.close),

      decreasing: { line: { color: "#7F7F7F" } },

      high: totalViewIv.underlying.map((d) => d.high),

      increasing: { line: { color: "#17BECF" } },

      low: totalViewIv.underlying.map((d) => d.low),

      open: totalViewIv.underlying.map((d) => d.open),

      customdata: totalViewIv.underlying.map((d) => d),
      hovertemplate:
        "Price: <b>%{customdata.average:.2f}</b> (%{customdata.low:.2f} - %{customdata.high:.2f})<br>" +
        "Volume: <b>%{customdata.volume}</b><br>" +
        "Time: <b>%{x}</b><br>" +
        "<extra></extra>",

      line: { color: "rgba(31,119,180,1)" },

      type: "candlestick",
      xaxis: "x",
      yaxis: "y",
    };

    setUnderlyingData([lineTrace, underlyingCandlesticks]);

    setPlotsLoading(false);
  }

  function resetState() {
    setError("");

    // reset data
    setOptIvData([]);
    setOptPriceData([]);
    setUnderlyingData([]);

    liveMarketDataStore.resetLiveMarketData();
    tradesStore.setTrades([]);
    positionsStore.setPositions([]);
    accountsStore.setAccounts([], null);
    frontendOptionState.setFrontendOptionState([]);
    orderModifyDataStore.reset();
    underlyingOrderModifyDataStore.reset();
    setUnderlyingConId(null);
  }

  function disconnectSocket() {
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
    resetState();
  }

  function refreshSocket() {
    resetState();

    setLoading(true);

    if (ws.current) {
      ws.current.close();
    }

    // Connect on mount. Always plain ws:// to the locally running IBKR backend,
    // even when the page is served over https (Chrome treats ws://localhost as
    // potentially trustworthy, so it isn't blocked as mixed content; Safari
    // does block it).
    ws.current = new WebSocket("ws://localhost:7878/ws");

    ws.current.onopen = () => {
      console.log("WebSocket connected");
      setWsConnected(true);
    };

    ws.current.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);

      // try to parse json
      try {
        const message = JSON.parse(event.data);

        if ("error" in message) {
          console.error("Server responded with error: ", message.error);
          setError("Server responded with error: " + message.error);
        }

        switch (message.function) {
          case "ping":
            setLastConnectionTime(message.time);
            break;
          case "get_trades":
            tradesStore.setTrades(message.result);
            break;
          case "get_totalview_iv":
            const data: IGetTotalViewIvResponse = message.result;

            setUnderlyingConId(data.underlyingConId);

            plotIV(data);

            frontendOptionState.setFrontendOptionState(
              data.options.map((opt) => ({
                readable_name: opt.readable_name,
                strike: opt.strike,
                right: opt.right,
                conId: opt.conId,
              })),
            );

            console.log("Received totalview_iv data:");
            break;
          case "place_order": {
            const resp: IPlaceOrderResponse = message.result;
            const pending = pendingOrderRef.current;
            if (pending && pending.conId === resp.conId.toString()) {
              if (pendingOrderTimerRef.current) {
                clearTimeout(pendingOrderTimerRef.current);
                pendingOrderTimerRef.current = null;
              }
              pendingOrderRef.current = null;
              orderModifyDataStore.setNewOrderModifyData(
                pending.conId,
                resp.orderId.toString(),
                pending.action,
                pending.readable_name,
                pending.price,
                (Number(pending.size) * Number(pending.price) * 100).toFixed(2),
                pending.size,
              );
            }
            const pendingUnderlying = pendingUnderlyingOrderRef.current;
            if (
              pendingUnderlying &&
              pendingUnderlying.conId === resp.conId.toString()
            ) {
              if (pendingUnderlyingOrderTimerRef.current) {
                clearTimeout(pendingUnderlyingOrderTimerRef.current);
                pendingUnderlyingOrderTimerRef.current = null;
              }
              pendingUnderlyingOrderRef.current = null;
              underlyingOrderModifyDataStore.setNewOrderModifyData(
                pendingUnderlying.conId,
                resp.orderId.toString(),
                pendingUnderlying.action,
                "",
                pendingUnderlying.price,
                (
                  Number(pendingUnderlying.price) *
                  Number(pendingUnderlying.size)
                ).toFixed(2),
                pendingUnderlying.size,
              );
            }
            break;
          }
          case "progress_message":
            setProgressMessage(message.result);
            break;
          case "market_data_update":
            liveMarketDataStore.updateLiveMarketData(message.result);
            break;
          case "option_solver":
            // update the IV in the orderModifyDataStore
            orderModifyDataStore.setIv(message.result.vol.toFixed(3));
            break;
          case "modify_order":
            break;
          case "get_positions":
            positionsStore.setPositions(message.result);
            break;
          case "accounts":
            accountsStore.setAccounts(message.result.accounts, message.result.selected);
            break;
          case "set_account":
            accountsStore.setSelectedAccount(message.result.selected);
            break;

          case "new_bar":
            const newBar = message.result;
            setUnderlyingData((prev) => {
              if (prev.length === 0) return prev;

              const lineTrace = prev.find((trace) => trace.type === "scatter");
              const candlestickTrace = prev.find(
                (trace) => trace.type === "candlestick",
              );

              if (!lineTrace || !candlestickTrace) return prev;

              const newDate = new Date(newBar.time);

              return [
                {
                  ...lineTrace,
                  x: [...lineTrace.x, newDate],
                  y: [...lineTrace.y, newBar.average],
                },
                {
                  ...candlestickTrace,
                  x: [...candlestickTrace.x, newDate],
                  open: [...candlestickTrace.open, newBar.open],
                  high: [...candlestickTrace.high, newBar.high],
                  low: [...candlestickTrace.low, newBar.low],
                  close: [...candlestickTrace.close, newBar.close],
                  customdata: [...candlestickTrace.customdata, newBar],
                },
              ];
            });
            break;

          case "updated_bar":
            const updatedBar = message.result;
            setUnderlyingData((prev) => {
              if (prev.length === 0) return prev;

              const lineTrace = prev.find((trace) => trace.type === "scatter");
              const candlestickTrace = prev.find(
                (trace) => trace.type === "candlestick",
              );

              if (!lineTrace || !candlestickTrace) return prev;

              const lastIdx = candlestickTrace.x.length - 1;

              const newOpen = [...candlestickTrace.open];
              const newHigh = [...candlestickTrace.high];
              const newLow = [...candlestickTrace.low];
              const newClose = [...candlestickTrace.close];
              const newCustomdata = [...candlestickTrace.customdata];
              const newY = [...lineTrace.y];

              newOpen[lastIdx] = updatedBar.open;
              newHigh[lastIdx] = updatedBar.high;
              newLow[lastIdx] = updatedBar.low;
              newClose[lastIdx] = updatedBar.close;
              newCustomdata[lastIdx] = updatedBar;
              newY[lastIdx] = updatedBar.average;

              return [
                { ...lineTrace, y: newY },
                {
                  ...candlestickTrace,
                  open: newOpen,
                  high: newHigh,
                  low: newLow,
                  close: newClose,
                  customdata: newCustomdata,
                },
              ];
            });
            break;

          default:
            setError("Unknown message function: " + message.function);
            break;
        }
      } catch (e) {
        setError("Error parsing WebSocket message");
        console.error("Error parsing WebSocket message:", e);
        return;
      } finally {
        setLoading(false);
      }
    };

    ws.current.onclose = () => {
      console.log("WebSocket disconnected");
      setWsConnected(false);
    };

    ws.current.onerror = (err) => {
      console.error("WebSocket error:", err);
      setError("WebSocket error: " + JSON.stringify(err));
    };

    setLoading(false);
  }

  function getTotalViewIv(
    e: React.FormEvent,
    reqOverride?: IGetTotalViewIvRequest,
  ) {
    e.preventDefault();
    setError("");

    resetState();

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      setError("WebSocket is not connected");
      return;
    }

    const req = reqOverride ?? optTotalViewReq;

    // validate inputs
    if (
      req.ticker === "" ||
      req.right === "" ||
      req.start === "" ||
      req.directional_limit === "" ||
      req.skip_expiries === "" ||
      req.number_of_expiries === ""
    ) {
      setError("All fields are required");
      return;
    }

    if (
      isNaN(Number(req.start)) ||
      isNaN(Number(req.directional_limit)) ||
      isNaN(Number(req.skip_expiries)) ||
      isNaN(Number(req.number_of_expiries))
    ) {
      setError(
        "Start, Directional Limit, Skip, and Number of Expiries must be numbers",
      );
      return;
    }

    // ensure directional_limit and number_of_expiries are integers
    if (
      !Number.isInteger(Number(req.directional_limit)) ||
      !Number.isInteger(Number(req.skip_expiries)) ||
      !Number.isInteger(Number(req.number_of_expiries))
    ) {
      setError(
        "Directional Limit, Skip, and Number of Expiries must be integers",
      );
      return;
    }

    setPlotsLoading(true);

    // Switching instrument via a secondary form: keep the "active" request
    // (used for the ticker shown in Positions/Active Trades filtering, and
    // persisted to localStorage) in sync with whichever form was submitted.
    if (reqOverride) {
      setOptTotalViewReq(req);
    }

    // send request
    const request = {
      function: "get_totalview_iv",
      ticker: req.ticker.toUpperCase(),
      right: req.right.toUpperCase(),
      start: Number(req.start),
      directional_limit: Number(req.directional_limit),
      skip_expiries: Number(req.skip_expiries),
      number_of_expiries: Number(req.number_of_expiries),
    };

    ws.current.send(JSON.stringify(request));
  }

  function cancelOrder(orderId: number) {
    setError("");

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      setError("WebSocket is not connected");
      return;
    }

    ws.current.send(
      JSON.stringify({
        function: "cancel_order",
        orderId: orderId,
      }),
    );
  }

  // Keep cancelOrderRef pointing at the current cancelOrder (uses ws ref internally, so safe)
  cancelOrderRef.current = cancelOrder;

  const [showOptionData, setShowOptionData] = useState(true);

  const containerRef = useRef<HTMLDivElement>(null);

  function readChartPriceAtY(
    chartRef: React.RefObject<HTMLDivElement | null>,
    clientY: number,
  ): number | null {
    const plotEl = chartRef.current?.querySelector(".js-plotly-plot") as any;
    if (!plotEl?._fullLayout) return null;
    const yaxis = plotEl._fullLayout.yaxis;
    if (!yaxis?.range || !yaxis._length) return null;
    const bb = plotEl.getBoundingClientRect();
    const relY = clientY - bb.top;
    if (relY < yaxis._offset || relY > yaxis._offset + yaxis._length) return null;
    const dataY = yaxis.range[1] - ((relY - yaxis._offset) / yaxis._length) * (yaxis.range[1] - yaxis.range[0]);
    return Math.max(0, dataY);
  }

  function handleUnderlyingChartMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (document.activeElement?.id !== "underlying-price") return;
    const dataY = readChartPriceAtY(underlyingChartRef, e.clientY);
    if (dataY === null) return;
    e.preventDefault();
    underlyingOrderModifyDataStore.setPrice(dataY.toFixed(2));
    document.getElementById("underlying-price")?.focus();
  }

  function handleOptionChartMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    if (document.activeElement?.id !== "price") return;
    const dataY = readChartPriceAtY(optPriceChartRef, e.clientY);
    if (dataY === null) return;
    e.preventDefault();
    orderModifyDataStore.setPrice(dataY.toFixed(2));
    orderModifyDataStore.setIv("Calculating...");
    document.getElementById("price")?.focus();
  }

  // Horizontal price-line overlays: draw a dashed line on the relevant
  // chart, colored to match the pending order's action (BUY/SELL), at
  // whatever price is currently in that form's Price field.
  function priceLineShapes(
    price: string,
    action: string,
  ): any[] | undefined {
    const priceNum = parseFloat(price);
    if (price === "" || isNaN(priceNum)) return undefined;
    const color =
      action === "BUY" ? "#2563eb" : action === "SELL" ? "#f97316" : undefined;
    if (!color) return undefined;

    return [
      {
        type: "line",
        xref: "paper",
        x0: 0,
        x1: 1,
        yref: "y",
        y0: priceNum,
        y1: priceNum,
        line: { color, width: 1.5, dash: "dot" },
      },
    ];
  }

  const underlyingPriceShapes = useMemo(
    () =>
      priceLineShapes(
        underlyingOrderModifyDataStore.underlyingOrderModifyData.price,
        underlyingOrderModifyDataStore.underlyingOrderModifyData.action,
      ),
    [
      underlyingOrderModifyDataStore.underlyingOrderModifyData.price,
      underlyingOrderModifyDataStore.underlyingOrderModifyData.action,
    ],
  );

  const optionPriceShapes = useMemo(
    () =>
      priceLineShapes(
        orderModifyDataStore.orderModifyData.price,
        orderModifyDataStore.orderModifyData.action,
      ),
    [
      orderModifyDataStore.orderModifyData.price,
      orderModifyDataStore.orderModifyData.action,
    ],
  );

  function focusPriceInput() {
    setTimeout(() => {
      const input =
        containerRef.current?.querySelector<HTMLInputElement>("#price");
      input?.focus();
      input?.select();
    }, 0);
  }

  // Keyboard shortcuts: b=buy, s=sell, m=load-last-active-order, c=cancel-all-active
  // Only fires when this Dockview panel is the active one and no input is focused
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === "TEXTAREA" || tag === "SELECT") return;
      if (tag === "INPUT" && active?.closest("[data-ibkr-get-form]")) return;
      if (props.containerApi.activePanel?.id !== props.api.id) return;

      switch (e.key) {
        case "b":
        case "s": {
          const first = latestOptionsRef.current[0];
          if (!first) return;
          orderModifyDataStore.setNewOrderModifyData(
            first.conId.toString(),
            "",
            e.key === "b" ? "BUY" : "SELL",
            first.readable_name,
            "",
            "",
            "",
          );
          focusPriceInput();
          break;
        }
        case "m": {
          const activeTrades = latestTradesRef.current.filter(
            (t) =>
              t.orderId !== 0 &&
              t.status !== "Filled" &&
              t.status !== "Cancelled",
          );
          const last = activeTrades[activeTrades.length - 1];
          if (!last) return;
          orderModifyDataStore.setNewOrderModifyData(
            last.conId.toString(),
            last.orderId.toString(),
            last.action,
            last.readable_name,
            last.lmtPrice.toFixed(2),
            (
              last.totalQuantity *
              last.lmtPrice *
              (last.readable_name.length > 6 ? 100 : 1)
            ).toFixed(2),
            last.totalQuantity.toString(),
          );
          focusPriceInput();
          break;
        }
        case "c": {
          const activeTrades = latestTradesRef.current.filter(
            (t) =>
              t.orderId !== 0 &&
              t.status !== "Filled" &&
              t.status !== "Cancelled",
          );
          activeTrades.forEach((t) => cancelOrderRef.current(t.orderId));
          break;
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function modifyOrder(conId: number, orderId: number, price: number) {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    ws.current.send(
      JSON.stringify({
        function: "modify_order",
        conId,
        orderId,
        new_lmtPrice: price,
        totalQuantity: Number(orderModifyDataStore.orderModifyData.size),
      }),
    );
  }

  function modifyOrCreateUnderlyingOrder(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      setError("WebSocket is not connected");
      return;
    }

    const { orderId, conId, action, price, size } =
      underlyingOrderModifyDataStore.underlyingOrderModifyData;

    if (price === "" || size === "") {
      setError("Price and Size are required");
      return;
    }

    if (isNaN(Number(price)) || isNaN(Number(size))) {
      setError("Price and Size must be numbers");
      return;
    }

    if (orderId !== "" && conId !== "") {
      ws.current.send(
        JSON.stringify({
          function: "modify_order",
          conId: Number(conId),
          orderId: Number(orderId),
          new_lmtPrice: Number(price),
          totalQuantity: Number(size),
        }),
      );
    } else {
      if (!underlyingConId) {
        setError("No underlying contract loaded");
        return;
      }
      if (pendingUnderlyingOrderTimerRef.current)
        clearTimeout(pendingUnderlyingOrderTimerRef.current);
      pendingUnderlyingOrderRef.current = {
        conId: underlyingConId.toString(),
        action,
        price,
        size,
      };
      pendingUnderlyingOrderTimerRef.current = setTimeout(() => {
        pendingUnderlyingOrderRef.current = null;
      }, 10_000);
      ws.current.send(
        JSON.stringify({
          function: "place_order",
          conId: underlyingConId,
          action,
          quantity: Number(size),
          lmtPrice: Number(price),
        }),
      );
    }
  }
  function modifyOrCreateOrder(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      setError("WebSocket is not connected");
      return;
    }

    // validate inputs
    if (
      orderModifyDataStore.orderModifyData.price === "" ||
      orderModifyDataStore.orderModifyData.size === ""
    ) {
      setError("Price and Size are required");
      return;
    }

    if (
      isNaN(Number(orderModifyDataStore.orderModifyData.price)) ||
      isNaN(Number(orderModifyDataStore.orderModifyData.size))
    ) {
      setError("Price and Size must be numbers");
      return;
    }

    // modify order
    if (
      orderModifyDataStore.orderModifyData.orderId !== "" &&
      orderModifyDataStore.orderModifyData.conId !== ""
    ) {
      const reqData = {
        function: "modify_order",
        conId: Number(orderModifyDataStore.orderModifyData.conId),
        orderId: Number(orderModifyDataStore.orderModifyData.orderId),
        new_lmtPrice: Number(orderModifyDataStore.orderModifyData.price),
        totalQuantity: Number(orderModifyDataStore.orderModifyData.size),
      };
      ws.current.send(JSON.stringify(reqData));
    } else {
      // create new order
      if (orderModifyDataStore.orderModifyData.conId === "") {
        setError("No contract selected for new order");
        return;
      }

      const { conId, action, readable_name, price, size } =
        orderModifyDataStore.orderModifyData;
      if (pendingOrderTimerRef.current)
        clearTimeout(pendingOrderTimerRef.current);
      pendingOrderRef.current = { conId, action, readable_name, price, size };
      pendingOrderTimerRef.current = setTimeout(() => {
        pendingOrderRef.current = null;
      }, 10_000);
      const reqData = {
        function: "place_order",
        conId: Number(conId),
        action,
        quantity: Number(size),
        lmtPrice: Number(price),
      };
      ws.current.send(JSON.stringify(reqData));
    }
  }

  if (loading) {
    return <div className="p-5">Loading...</div>;
  }

  return (
    <div ref={containerRef} className="p-5 h-full w-full flex flex-col">
      <div>
        {error && <div className="text-red-500 mt-2">Error: {error}</div>}
      </div>

      <div className="flex gap-4">
        <button className="btn btn-default" onClick={refreshSocket}>
          {wsConnected ? "Refresh socket" : "Connect socket"}
        </button>
        {wsConnected && (
          <button
            type="button"
            className="btn btn-default"
            onClick={disconnectSocket}
          >
            Disconnect
          </button>
        )}
        {accountsStore.accounts.length > 1 && (
          <select
            className="btn btn-default"
            value={accountsStore.selectedAccount ?? ""}
            onChange={(e) => {
              const account = e.target.value;
              accountsStore.setSelectedAccount(account);
              ws.current?.send(JSON.stringify({ function: "set_account", account }));
            }}
          >
            <option value="" disabled>
              Select account
            </option>
            {accountsStore.accounts.map((account) => (
              <option key={account} value={account}>
                {account}
              </option>
            ))}
          </select>
        )}
        <div>
          {lastConnectionTime} {progressMessage}
        </div>
      </div>

      <div className="overflow-auto grow">
        <GetTotalIvForm
          formId="primary"
          optTotalViewReq={optTotalViewReq}
          setOptTotalViewReq={setOptTotalViewReq}
          getTotalViewIv={getTotalViewIv}
          onAdd={() => addTotalViewFormAfter(optTotalViewReq, -1)}
        />

        {extraTotalViewForms.map((form, i) => (
          <GetTotalIvForm
            key={form.id}
            formId={form.id}
            optTotalViewReq={form.req}
            setOptTotalViewReq={(update) =>
              updateTotalViewForm(
                form.id,
                typeof update === "function"
                  ? (update as (prev: IGetTotalViewIvRequest) => IGetTotalViewIvRequest)(
                      form.req,
                    )
                  : update,
              )
            }
            getTotalViewIv={(e) => getTotalViewIv(e, form.req)}
            onAdd={() => addTotalViewFormAfter(form.req, i)}
            onRemove={() => removeTotalViewForm(form.id)}
          />
        ))}

        <div>
          <div ref={underlyingChartRef} onMouseDown={handleUnderlyingChartMouseDown}>
            <Plotter
              data={underlyingData}
              layout={underlyingLayout}
              isLoading={plotsLoading}
              shapes={underlyingPriceShapes}
            />
          </div>

          <label className="flex items-center gap-2 my-2 cursor-pointer select-none text-sm">
            <input
              type="checkbox"
              checked={showOptionData}
              onChange={(e) => setShowOptionData(e.target.checked)}
            />
            Show option data
          </label>

          {showOptionData && (
            <>
              <b>Option Prices:</b>
              <div ref={optPriceChartRef} onMouseDown={handleOptionChartMouseDown}>
                <Plotter
                  data={optPriceData}
                  layout={optPricingLayout}
                  isLoading={plotsLoading}
                  shapes={optionPriceShapes}
                />
              </div>

              <b>Option IV:</b>
              <Plotter
                data={optIvData}
                layout={optIvLayout}
                isLoading={plotsLoading}
              />
            </>
          )}
        </div>

        <hr />

        <div className="flex w-full gap-8">
          {/* List option contracts by name and as positioned in the layout */}
          <div className="w-[50%]">
            <b>Contract selector:</b>
            <ContractSelector />
          </div>

          {/* Currently active trades */}
          <div className=" w-[40%]">
            <PositionsList ticker={optTotalViewReq.ticker.toUpperCase()} />
            <ActiveTrades
              cancelOrder={cancelOrder}
              ticker={optTotalViewReq.ticker.toUpperCase()}
            />
          </div>
        </div>

        {/* Mirrors the keyboard handler above (active panel, no input focused) */}
        <p className="text-xs text-gray-500 mt-2">
          <b>Shortcuts:</b> <kbd>b</kbd> buy first contract · <kbd>s</kbd> sell
          first contract · <kbd>m</kbd> modify last active order · <kbd>c</kbd>{" "}
          cancel all active orders · <kbd>↑</kbd>/<kbd>↓</kbd> in Price: step by
          Increment
        </p>
      </div>

      {/* Submit / edit order */}
      <div>
        <hr className="" />
        <p className="font-semibold">Underlying</p>
        <UnderlyingOrder
          modifyOrCreateUnderlyingOrder={modifyOrCreateUnderlyingOrder}
        />

        <p className="font-semibold">Option</p>
        <OrderInput modifyOrCreateOrder={modifyOrCreateOrder} />
      </div>
    </div>
  );
}

const PositionsList = ({ ticker }: { ticker: string }) => {
  const positionsStore = usePositionsStore();
  const orderModifyDataStore = useOrderModifyDataStore();
  const underlyingOrderModifyDataStore = useUnderlyingOrderModifyDataStore();
  const frontendOptionState = useFrontendOptionState();

  function handlePositionClick(pos: { conId: number; readable_name: string; size: number; avgCost: number }) {
    const action = pos.size < 0 ? "BUY" : "SELL";
    const absSize = Math.abs(pos.size).toString();
    const isOption = pos.readable_name.length > 6;

    if (isOption) {
      const inSelector = frontendOptionState.frontendOptionState.find((o) => o.conId === pos.conId);
      if (!inSelector) return;
      const price = (pos.avgCost / 100).toFixed(2);
      const total = (pos.avgCost * Math.abs(pos.size)).toFixed(2);
      orderModifyDataStore.setNewOrderModifyData(
        pos.conId.toString(), "", action, pos.readable_name, price, total, absSize,
      );
    } else {
      if (pos.readable_name.toUpperCase() !== ticker) return;
      underlyingOrderModifyDataStore.setAction(action);
      underlyingOrderModifyDataStore.setPrice(pos.avgCost.toFixed(2));
      underlyingOrderModifyDataStore.setSize(absSize);
    }
  }

  return (
    <div>
      <b>Positions:</b>
      {positionsStore.positions.length === 0 && (
        <p className="text-gray-500">Empty</p>
      )}

      {positionsStore.positions.map((pos) => {
        const isOption = pos.readable_name.length > 6;
        const clickable = isOption
          ? frontendOptionState.frontendOptionState.some((o) => o.conId === pos.conId)
          : pos.readable_name.toUpperCase() === ticker;
        return (
          <div
            key={pos.conId}
            className={clickable ? "cursor-pointer hover:underline" : ""}
            onClick={clickable ? () => handlePositionClick(pos) : undefined}
          >
            <p>
              {pos.readable_name} P=
              {(pos.avgCost / (isOption ? 100 : 1)).toFixed(2)}{" "}
              S={pos.size}
            </p>
          </div>
        );
      })}
    </div>
  );
};
